import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import {
  CheckCircle, Circle, X, Clock, Download, Upload,
  Warning, Certificate, Student, ChalkboardTeacher, QrCode,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const CATEGORIES = ["All", "Computer", "Electrical", "Electronics", "Solar", "Health", "Food", "Dress Making"];

const T = {
  en: {
    hero_tag: "SETTI — Self Employment Technical Training Institute",
    hero_sub: "An initiative of ANVESHAN SOCIETY. Free lessons, strict assessments, real skills.",
    teacher_mode: "Teacher mode", learner_mode: "Learner mode",
    continue: "Continue learning", enrolled: "Enrolled", enroll: "Enroll — free",
    legacy_tag: "Legacy enrollment",
    legacy_note: "Your progress is preserved, but a short final assessment is required to earn a certificate.",
    view_lessons: "View lessons", lessons: "Lessons", assignments: "Assignments",
    assessment: "Final Assessment", submit: "Submit",
    graded_pass: "Approved", graded_fail: "Needs revision", graded_pending: "Awaiting grading",
    graded_needs_revision: "Needs revision", graded_complete: "Complete",
    start_assessment: "Start assessment", take_reassessment: "Take re-assessment",
    submit_assessment: "Submit assessment", passing: "Passing: 60%",
    scored: "You scored", weak_topics: "Weak topics", recommended: "Recommended re-learning",
    cert_ready: "Certificate is ready", cert_download: "Download PDF",
    cert_not_eligible: "Not yet eligible", verify_cert: "Verify",
    reasons: {
      not_enrolled: "You are not enrolled.",
      lessons_incomplete: "Complete all mandatory lessons.",
      assignments_missing: "Submit all mandatory assignments.",
      grading_pending: "Some assignments are still being graded.",
      assignments_failed: "At least one assignment needs revision.",
      assessment_not_taken: "Take the final assessment.",
      assessment_below_threshold: "Achieve at least 60% in the final assessment.",
      integrity_hold: "There is an academic integrity hold on your account.",
    },
    mandatory: "Mandatory", optional: "Optional",
    marks: "Marks", attempts: "Attempt history",
    issue_cert: "Issue my certificate",
    cert_will_issue: "Pass the assessment and submit all assignments to earn the SETTI certificate.",
    create_course: "Create course", my_courses: "My courses", grading_inbox: "Grading inbox",
    add_lesson: "Add lesson", add_assignment: "Add assignment",
    publish: "Publish", unpublish: "Unpublish",
    status_draft: "Draft", status_published: "Published",
    close: "Close", save: "Save", delete: "Delete",
    week: "Week", pct_complete: "complete",
    text_response: "Write your answer…", upload_file: "Upload file",
  },
  hi: {
    hero_tag: "SETTI — स्वरोज़गार तकनीकी प्रशिक्षण संस्थान",
    hero_sub: "अन्वेषण सोसाइटी की पहल। मुफ़्त पाठ, सख़्त मूल्यांकन, असली कौशल।",
    teacher_mode: "शिक्षक मोड", learner_mode: "विद्यार्थी मोड",
    continue: "सीखना जारी रखें", enrolled: "पंजीकृत", enroll: "पंजीकरण — मुफ़्त",
    legacy_tag: "पुरानी नामांकन",
    legacy_note: "आपकी प्रगति सुरक्षित है, लेकिन प्रमाण-पत्र के लिए अंतिम मूल्यांकन अनिवार्य है।",
    view_lessons: "पाठ देखें", lessons: "पाठ", assignments: "असाइनमेंट",
    assessment: "अंतिम मूल्यांकन", submit: "जमा करें",
    graded_pass: "स्वीकृत", graded_fail: "संशोधन चाहिए", graded_pending: "मूल्यांकन लंबित",
    graded_needs_revision: "संशोधन चाहिए", graded_complete: "पूर्ण",
    start_assessment: "मूल्यांकन शुरू करें", take_reassessment: "पुनः मूल्यांकन दें",
    submit_assessment: "मूल्यांकन जमा करें", passing: "उत्तीर्ण: 60%",
    scored: "आपके अंक", weak_topics: "कमज़ोर विषय", recommended: "सुझाया गया पुनः-अध्ययन",
    cert_ready: "प्रमाण-पत्र तैयार है", cert_download: "PDF डाउनलोड",
    cert_not_eligible: "अभी पात्र नहीं", verify_cert: "सत्यापित करें",
    reasons: {
      not_enrolled: "आप नामांकित नहीं हैं।",
      lessons_incomplete: "सभी अनिवार्य पाठ पूरे करें।",
      assignments_missing: "सभी अनिवार्य असाइनमेंट जमा करें।",
      grading_pending: "कुछ असाइनमेंट अभी भी मूल्यांकन में हैं।",
      assignments_failed: "कम-से-कम एक असाइनमेंट में संशोधन चाहिए।",
      assessment_not_taken: "अंतिम मूल्यांकन लें।",
      assessment_below_threshold: "अंतिम मूल्यांकन में कम-से-कम 60% अंक लाएँ।",
      integrity_hold: "आपके खाते पर academic hold है।",
    },
    mandatory: "अनिवार्य", optional: "वैकल्पिक",
    marks: "अंक", attempts: "प्रयास इतिहास",
    issue_cert: "प्रमाण-पत्र जारी करें",
    cert_will_issue: "SETTI प्रमाण-पत्र पाने के लिए मूल्यांकन पास करें व सभी असाइनमेंट जमा करें।",
    create_course: "कोर्स बनाएँ", my_courses: "मेरे कोर्स", grading_inbox: "मूल्यांकन इनबॉक्स",
    add_lesson: "पाठ जोड़ें", add_assignment: "असाइनमेंट जोड़ें",
    publish: "प्रकाशित करें", unpublish: "अप्रकाशित करें",
    status_draft: "मसौदा", status_published: "प्रकाशित",
    close: "बंद करें", save: "सहेजें", delete: "हटाएँ",
    week: "सप्ताह", pct_complete: "पूर्ण",
    text_response: "अपना उत्तर लिखें…", upload_file: "फ़ाइल अपलोड करें",
  },
};

export default function Training() {
  const { user } = useAuth();
  const { lang } = useLang();
  const t = T[lang] || T.en;
  const [mode, setMode] = useState("learner");
  const [courses, setCourses] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [category, setCategory] = useState("All");
  const [detailId, setDetailId] = useState(null);
  const [params] = useSearchParams();

  const loadCourses = useCallback(async () => {
    const { data } = await api.get("/setti/courses", {
      params: category === "All" ? {} : { category },
    });
    setCourses(data);
  }, [category]);

  const loadEnrollments = useCallback(async () => {
    if (!user || user === false) return;
    try {
      const { data } = await api.get("/setti/enrollments/mine");
      setEnrollments(data);
    } catch (err) { console.error("load enrollments", err); }
  }, [user]);

  useEffect(() => { loadCourses(); }, [loadCourses]);
  useEffect(() => { loadEnrollments(); }, [loadEnrollments]);

  useEffect(() => {
    const pid = params.get("course") || params.get("program");
    if (pid) setDetailId(pid);
  }, [params]);

  const canTeach = user && (user.user_type === "teacher" || user.role === "admin");

  if (detailId) {
    return <CourseDetail courseId={detailId} onClose={() => { setDetailId(null); loadEnrollments(); }} t={t} />;
  }

  const getEnrollment = (cid) => enrollments.find((e) => e.course_id === cid);

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#D95A41]" data-testid="setti-tag">{t.hero_tag}</div>
      <h1 className="font-heading text-5xl mt-2 tracking-tight">
        <em className="text-[#D95A41] not-italic">Learn.</em> Prove. Earn a certificate.
      </h1>
      <p className="text-[#5C564D] mt-2 max-w-2xl">{t.hero_sub}</p>

      {canTeach && (
        <div className="mt-6 inline-flex gap-1 bg-[#F2EBE5] rounded-full p-1" data-testid="mode-switch">
          <button data-testid="mode-learner" onClick={() => setMode("learner")}
            className={`px-4 py-1.5 rounded-full text-sm font-semibold ${mode === "learner" ? "bg-[#2C4A3B] text-[#F9F6F0]" : "text-[#5C564D]"}`}>
            <Student size={14} className="inline mr-1" /> {t.learner_mode}
          </button>
          <button data-testid="mode-teacher" onClick={() => setMode("teacher")}
            className={`px-4 py-1.5 rounded-full text-sm font-semibold ${mode === "teacher" ? "bg-[#2C4A3B] text-[#F9F6F0]" : "text-[#5C564D]"}`}>
            <ChalkboardTeacher size={14} className="inline mr-1" /> {t.teacher_mode}
          </button>
        </div>
      )}

      {mode === "teacher" && canTeach ? (
        <TeacherDashboard t={t} />
      ) : (
        <>
          {user && user !== false && enrollments.length > 0 && (
            <div className="mt-10">
              <div className="text-xs tracking-[0.15em] uppercase font-bold text-[#2C4A3B]">{t.continue}</div>
              <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="my-enrollments">
                {enrollments.map((e) => {
                  const total = Math.max(e.total_lessons || 1, 1);
                  const pct = Math.round((e.completed_lessons / total) * 100);
                  return (
                    <div key={e.id} className="samaaj-card p-5" data-testid={`enroll-${e.course_id}`}>
                      <div className="font-heading text-lg text-[#1C1B1A]">{e.course_title}</div>
                      <div className="text-xs text-[#5C564D] mt-1 flex items-center gap-2">
                        <Clock size={12} /> {e.duration_weeks || "-"} wks • {e.category}
                      </div>
                      {e.legacy_migrated && (
                        <div className="mt-2 inline-flex items-center gap-1 bg-[#F2EBE5] text-[#8C6B2E] text-[10px] font-bold px-2 py-0.5 rounded-full">
                          <Warning size={10} weight="bold" /> {t.legacy_tag}
                        </div>
                      )}
                      <div className="mt-3 h-2 rounded-full bg-[#E5D9CC] overflow-hidden">
                        <div className="h-full" style={{ width: `${pct}%`, background: "#2C4A3B", transition: "width 200ms ease-out" }} />
                      </div>
                      <div className="mt-2 flex items-center justify-between text-xs text-[#5C564D]">
                        <span>{e.completed_lessons} / {total} {t.lessons.toLowerCase()}</span>
                        <span className="font-bold text-[#2C4A3B]">{pct}%</span>
                      </div>
                      <button onClick={() => setDetailId(e.course_id)} className="samaaj-btn-outline w-full mt-3 text-sm" data-testid={`continue-${e.course_id}`}>
                        {t.continue}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="mt-10 flex flex-wrap gap-2" data-testid="train-cats">
            {CATEGORIES.map((c) => (
              <button key={c} data-testid={`train-cat-${c}`} onClick={() => setCategory(c)}
                className={`px-4 py-2 rounded-full text-sm font-semibold ${category === c ? "bg-[#D95A41] text-[#F9F6F0]" : "bg-[#F2EBE5] text-[#D95A41]"}`}>
                {c}
              </button>
            ))}
          </div>

          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="programs-grid">
            {courses.map((p) => {
              const en = getEnrollment(p.id);
              return (
                <div key={p.id} className="samaaj-card overflow-hidden" data-testid={`program-${p.id}`}>
                  <div className="aspect-[16/9] relative">
                    <img src={p.cover || "https://images.unsplash.com/photo-1497486751825-1233686d5d80?auto=format&fit=crop&w=800&q=80"} alt={p.title} className="w-full h-full object-cover" />
                    <div className="absolute top-3 right-3 px-3 py-1 rounded-full text-xs font-bold text-[#F9F6F0]" style={{ background: "#2C4A3B" }}>Free</div>
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 text-xs">
                      <span className="samaaj-chip">{p.category}</span>
                      <span className="text-[#5C564D]">{p.duration_weeks} wks • {p.level}</span>
                    </div>
                    <div className="font-heading text-xl mt-2 text-[#1C1B1A]">{p.title}</div>
                    <p className="text-sm text-[#5C564D] mt-2 line-clamp-2">{p.description}</p>
                    <div className="mt-3 text-[11px] text-[#5C564D]">
                      {p.lesson_count || 0} {t.lessons.toLowerCase()} · {p.enrolled_count || 0} learners
                    </div>
                    <div className="mt-4 flex items-center gap-2">
                      <button onClick={() => setDetailId(p.id)} data-testid={`btn-open-${p.id}`} className="samaaj-btn-outline text-sm flex-1">
                        {t.view_lessons}
                      </button>
                      {en ? <span className="text-xs font-semibold text-[#2C4A3B]">{t.enrolled}</span> : null}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

/* ---------- Learner course detail ---------- */
function CourseDetail({ courseId, onClose, t }) {
  const [dash, setDash] = useState(null);
  const [busy, setBusy] = useState(false);
  const [assessment, setAssessment] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get(`/setti/dashboard/${courseId}`);
      setDash(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, [courseId]);
  useEffect(() => { refresh(); }, [refresh]);

  if (!dash) return <div className="max-w-5xl mx-auto px-6 py-16 text-center text-[#5C564D]">Loading…</div>;
  const course = dash.course;
  const enrolled = dash.enrolled;
  const totalLessons = dash.lessons.length;
  const completedSet = new Set(dash.completed_lesson_ids);
  const pct = totalLessons ? Math.round((dash.completed_lesson_ids.length / totalLessons) * 100) : 0;
  const elig = dash.eligibility;

  const enroll = async () => {
    try { await api.post("/setti/enroll", { course_id: courseId }); toast.success("Enrolled."); refresh(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const toggleLesson = async (lid, done) => {
    try {
      if (done) await api.post("/setti/lessons/uncomplete", { lesson_id: lid });
      else await api.post("/setti/lessons/complete", { lesson_id: lid });
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const startAssessment = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/setti/assessments/start", { course_id: courseId });
      setAssessment(data); setAnswers({}); setResult(null);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const submitAssessment = async () => {
    if (Object.keys(answers).length < assessment.questions.length) { toast.error("Please answer every question."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/setti/assessments/submit", { attempt_id: assessment.attempt_id, answers });
      setResult(data);
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const issueCert = async () => {
    try {
      const { data } = await api.post("/setti/certificates/issue", { course_id: courseId });
      toast.success(`Certificate ${data.certificate_no} issued!`);
      refresh();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (typeof detail === "object" && detail?.message) toast.error(detail.message);
      else toast.error(formatApiErrorDetail(detail));
    }
  };
  const downloadCert = async (certId) => {
    try {
      const resp = await api.get(`/setti/certificates/${certId}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `setti-cert-${certId}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Failed to download"); }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-10" data-testid="course-detail">
      <button onClick={onClose} className="text-sm text-[#5C564D] mb-4" data-testid="close-course">← Back</button>
      <div className="samaaj-card p-6 bg-[#F9F6F0]">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#D95A41]">{course.category}</div>
            <h2 className="font-heading text-3xl mt-1">{course.title}</h2>
            <div className="text-sm text-[#5C564D] mt-1">{course.duration_weeks} weeks • {course.level}</div>
            <p className="text-[#1C1B1A] mt-3 leading-relaxed max-w-2xl">{course.description}</p>
          </div>
          {!enrolled ? (
            <button onClick={enroll} className="samaaj-btn-primary" data-testid="detail-enroll">{t.enroll}</button>
          ) : (
            <div className="text-right">
              <div className="text-xs text-[#5C564D]">{t.pct_complete}</div>
              <div className="font-heading text-3xl text-[#2C4A3B]">{pct}%</div>
            </div>
          )}
        </div>

        {dash.legacy_migrated && (
          <div className="mt-4 rounded-xl bg-[#FBF1DF] border border-[#D49A36]/40 p-3 text-sm text-[#8C6B2E]">
            <Warning className="inline mr-1" size={14} weight="bold" /> {t.legacy_note}
          </div>
        )}

        {enrolled && (
          <>
            <EligibilityPanel elig={elig} cert={dash.certificate} t={t} onIssue={issueCert} onDownload={downloadCert} />

            <div className="mt-8">
              <div className="text-xs tracking-[0.15em] uppercase font-bold text-[#2C4A3B]">{t.lessons}</div>
              <ul className="mt-3 space-y-2" data-testid="lessons-list">
                {dash.lessons.map((l, i) => {
                  const done = completedSet.has(l.id);
                  const recommended = elig?.recommended_lesson_ids?.includes(l.id);
                  return (
                    <li key={l.id} className={`rounded-xl border overflow-hidden ${recommended ? "border-[#D49A36] bg-[#FBF1DF]" : "border-[#E5D9CC] bg-[#F2EBE5]"}`} data-testid={`lesson-${l.id}`}>
                      <div className="flex items-center gap-3 p-3">
                        {done ? <CheckCircle size={20} weight="fill" className="text-[#2C4A3B]" /> : <Circle size={20} className="text-[#5C564D]" />}
                        <div className="flex-1">
                          <div className="text-sm font-semibold text-[#1C1B1A]">
                            {t.week} {i + 1}: {l.title}
                            {!l.is_mandatory && <span className="ml-2 text-[10px] text-[#5C564D]">({t.optional})</span>}
                            {recommended && <span className="ml-2 text-[10px] text-[#D49A36] font-bold">★ {t.recommended}</span>}
                          </div>
                          {l.content && <div className="text-xs text-[#5C564D] mt-0.5">{l.content}</div>}
                        </div>
                        <button onClick={() => toggleLesson(l.id, done)}
                          className={done ? "samaaj-btn-outline text-xs" : "samaaj-btn-primary text-xs"}
                          data-testid={`toggle-${l.id}`}>
                          {done ? "Undo" : "Mark done"}
                        </button>
                      </div>
                      {l.video_url && <LessonVideo url={l.video_url} />}
                    </li>
                  );
                })}
              </ul>
            </div>

            <div className="mt-8">
              <div className="text-xs tracking-[0.15em] uppercase font-bold text-[#2C4A3B]">{t.assignments}</div>
              <div className="mt-3 space-y-3" data-testid="assignments-list">
                {dash.assignments.map((a) => (
                  <AssignmentCard key={a.id} assignment={a} submission={dash.submissions_by_assignment[a.id]} t={t} onRefresh={refresh} />
                ))}
              </div>
            </div>

            <div className="mt-8" data-testid="assessment-section">
              <div className="text-xs tracking-[0.15em] uppercase font-bold text-[#2C4A3B]">{t.assessment} · {t.passing}</div>
              {dash.attempts.length > 0 && (
                <div className="mt-3 rounded-xl bg-[#F2EBE5] border border-[#E5D9CC] p-3">
                  <div className="text-xs font-bold text-[#5C564D] mb-1">{t.attempts}</div>
                  {dash.attempts.map((at, i) => (
                    <div key={at.id} className="text-xs flex items-center gap-2" data-testid={`attempt-${i}`}>
                      <span className={at.passed ? "text-[#2C4A3B] font-bold" : "text-[#D95A41]"}>
                        {Math.round(at.score_percent)}% {at.passed ? "✓" : "✗"}
                      </span>
                      <span className="text-[#5C564D]">· {at.correct}/{at.total} correct · {(at.submitted_at || "").slice(0,10)}</span>
                    </div>
                  ))}
                </div>
              )}
              {!assessment && (
                <button onClick={startAssessment} disabled={busy} className="samaaj-btn-primary mt-3" data-testid="btn-start-assessment">
                  {dash.attempts.length === 0 ? t.start_assessment : t.take_reassessment}
                </button>
              )}
              {assessment && !result && (
                <div className="mt-3 space-y-4" data-testid="assessment-form">
                  {assessment.questions.map((q, qi) => (
                    <div key={q.id} className="samaaj-card p-4 bg-[#F9F6F0]">
                      <div className="font-semibold text-sm mb-2">{qi + 1}. {q.text}</div>
                      <div className="space-y-1.5">
                        {q.options.map((opt, oi) => (
                          <label key={oi} className="flex items-center gap-2 text-sm cursor-pointer">
                            <input type="radio" name={q.id} value={oi}
                              checked={answers[q.id] === oi}
                              onChange={() => setAnswers({ ...answers, [q.id]: oi })}
                              data-testid={`q-${qi}-opt-${oi}`} />
                            {opt}
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                  <button onClick={submitAssessment} disabled={busy} className="samaaj-btn-primary" data-testid="btn-submit-assessment">
                    {busy ? "Submitting…" : t.submit_assessment}
                  </button>
                </div>
              )}
              {result && (
                <div className={`mt-3 rounded-xl p-4 border ${result.passed ? "bg-[#E6F0E8] border-[#2C4A3B]" : "bg-[#FBE3DC] border-[#D95A41]"}`} data-testid="assessment-result">
                  <div className="font-heading text-2xl">
                    {t.scored} {Math.round(result.score_percent)}% · {result.correct}/{result.total}
                  </div>
                  <div className="text-sm mt-1">
                    {result.passed ? "✓ You passed — complete remaining assignments to earn the certificate." : "You did not reach 60%. Review the recommended lessons and try again."}
                  </div>
                  {result.weak_topics?.length > 0 && (
                    <div className="text-xs mt-2 text-[#5C564D]"><b>{t.weak_topics}:</b> {result.weak_topics.join(", ")}</div>
                  )}
                  <button className="samaaj-btn-outline mt-3 text-sm" onClick={() => { setAssessment(null); setResult(null); }} data-testid="assessment-done">Done</button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function EligibilityPanel({ elig, cert, t, onIssue, onDownload }) {
  if (!elig) return null;
  const Checklist = ({ ok, label }) => (
    <div className="flex items-center gap-2 text-sm">
      {ok ? <CheckCircle size={16} weight="fill" className="text-[#2C4A3B]" /> : <Circle size={16} className="text-[#5C564D]" />}
      <span className={ok ? "text-[#1C1B1A]" : "text-[#5C564D]"}>{label}</span>
    </div>
  );
  const p = elig.progress || {};
  const scoreOk = elig.latest_score != null && elig.latest_score >= 60;
  return (
    <div className="mt-6 rounded-2xl p-5 border-2" style={{ borderColor: elig.eligible ? "#2C4A3B" : "#E5D9CC", background: elig.eligible ? "#E6F0E8" : "#F9F6F0" }} data-testid="eligibility-panel">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs tracking-[0.15em] uppercase font-bold text-[#2C4A3B]">
            <Certificate size={14} className="inline mr-1" weight="fill" />
            {cert ? t.cert_ready : (elig.eligible ? t.cert_ready : t.cert_not_eligible)}
          </div>
          <div className="font-heading text-xl mt-1">
            {cert ? `Certificate ${cert.certificate_no}` : t.cert_will_issue}
          </div>
        </div>
        {cert ? (
          <div className="flex gap-2">
            <button onClick={() => onDownload(cert.id)} className="samaaj-btn-primary text-sm" data-testid="btn-download-cert">
              <Download size={14} className="inline mr-1" /> {t.cert_download}
            </button>
            <a href={`/certificates/verify/${cert.verify_token}`} target="_blank" rel="noreferrer" className="samaaj-btn-outline text-sm" data-testid="btn-verify-cert">
              <QrCode size={14} className="inline mr-1" /> {t.verify_cert}
            </a>
          </div>
        ) : elig.eligible ? (
          <button onClick={onIssue} className="samaaj-btn-primary text-sm" data-testid="btn-issue-cert">
            <Certificate size={14} className="inline mr-1" /> {t.issue_cert}
          </button>
        ) : null}
      </div>
      <div className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-1">
        <Checklist ok={p.completed_mandatory_lessons === p.total_mandatory_lessons} label={`${t.lessons} ${p.completed_mandatory_lessons}/${p.total_mandatory_lessons}`} />
        <Checklist ok={p.submitted_mandatory_assignments === p.total_mandatory_assignments} label={`${t.assignments} ${p.submitted_mandatory_assignments}/${p.total_mandatory_assignments}`} />
        <Checklist ok={p.pending_grading === 0} label={p.pending_grading ? `${p.pending_grading} ${t.graded_pending.toLowerCase()}` : "All graded"} />
        <Checklist ok={scoreOk} label={`${t.marks} ${elig.latest_score != null ? Math.round(elig.latest_score) + "%" : "—"} / 60%`} />
        <Checklist ok={!elig.integrity_hold} label="No integrity hold" />
        <Checklist ok={p.failed_assignments === 0} label={p.failed_assignments ? `${p.failed_assignments} assignment(s) need revision` : "All assignments approved"} />
      </div>
      {!elig.eligible && elig.reasons?.length > 0 && (
        <div className="mt-3 text-xs text-[#5C564D]">
          <b>Next steps:</b> {elig.reasons.map((r) => t.reasons[r] || r).join(" ")}
        </div>
      )}
      {elig.weak_topics?.length > 0 && (
        <div className="mt-2 text-xs text-[#D95A41]"><b>{t.weak_topics}:</b> {elig.weak_topics.join(", ")}</div>
      )}
    </div>
  );
}

function LessonVideo({ url }) {
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{11})/);
  const vimeo = url.match(/vimeo\.com\/(\d+)/);
  return (
    <div className="aspect-video w-full bg-black">
      {yt ? (
        <iframe title="lesson" src={`https://www.youtube.com/embed/${yt[1]}`} allowFullScreen className="w-full h-full" />
      ) : vimeo ? (
        <iframe title="lesson" src={`https://player.vimeo.com/video/${vimeo[1]}`} allowFullScreen className="w-full h-full" />
      ) : (
        <video controls src={url} className="w-full h-full" preload="metadata" />
      )}
    </div>
  );
}

function AssignmentCard({ assignment, submission, t, onRefresh }) {
  const [text, setText] = useState("");
  const [fileUrl, setFileUrl] = useState("");
  const [busy, setBusy] = useState(false);

  const uploadFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    setBusy(true);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setFileUrl(data.url);
      toast.success("Uploaded");
    } catch (err) { toast.error("Upload failed"); }
    finally { setBusy(false); }
  };

  const submit = async () => {
    if (!text.trim() && !fileUrl) { toast.error("Add text or a file."); return; }
    setBusy(true);
    try {
      await api.post("/setti/submissions", { assignment_id: assignment.id, text_response: text, file_url: fileUrl });
      toast.success("Submitted");
      setText(""); setFileUrl("");
      onRefresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const statusMap = {
    pending: { label: t.graded_pending, color: "#8C6B2E", bg: "#FBF1DF" },
    pass: { label: t.graded_pass, color: "#2C4A3B", bg: "#E6F0E8" },
    complete: { label: t.graded_complete, color: "#2C4A3B", bg: "#E6F0E8" },
    fail: { label: t.graded_fail, color: "#D95A41", bg: "#FBE3DC" },
    needs_revision: { label: t.graded_needs_revision, color: "#D95A41", bg: "#FBE3DC" },
  };
  const st = submission ? statusMap[submission.grade_status] : null;

  return (
    <div className="samaaj-card p-4 bg-[#F9F6F0]" data-testid={`assignment-${assignment.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="font-semibold text-[#1C1B1A]">
            {assignment.title}
            {assignment.is_mandatory && <span className="ml-2 text-[10px] font-bold text-[#D95A41] uppercase">{t.mandatory}</span>}
          </div>
          <div className="text-xs text-[#5C564D] mt-1">{assignment.instructions}</div>
        </div>
        {st && (
          <div className="px-2 py-1 rounded-full text-[10px] font-bold" style={{ color: st.color, background: st.bg }} data-testid={`sub-status-${assignment.id}`}>
            {st.label}
          </div>
        )}
      </div>
      {submission && (
        <div className="mt-3 text-xs rounded-lg bg-[#F2EBE5] p-3">
          {submission.text_response && <div className="whitespace-pre-wrap">{submission.text_response}</div>}
          {submission.file_url && <a href={submission.file_url} target="_blank" rel="noreferrer" className="text-[#D95A41] font-semibold">📎 Attachment</a>}
          {submission.teacher_notes && <div className="mt-2 text-[#5C564D]"><b>Teacher:</b> {submission.teacher_notes}</div>}
        </div>
      )}
      {(!submission || submission.grade_status === "needs_revision" || submission.grade_status === "fail") && (
        <div className="mt-3 space-y-2">
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder={t.text_response}
            className="w-full px-3 py-2 rounded-xl border border-[#E5D9CC] text-sm" data-testid={`sub-text-${assignment.id}`} />
          <div className="flex items-center gap-2 flex-wrap">
            <label className="samaaj-btn-outline text-xs cursor-pointer" data-testid={`sub-file-${assignment.id}`}>
              <Upload size={12} className="inline mr-1" /> {t.upload_file}
              <input type="file" className="hidden" onChange={uploadFile} />
            </label>
            {fileUrl && <span className="text-xs text-[#2C4A3B]">✓ Attached</span>}
            <button onClick={submit} disabled={busy} className="samaaj-btn-primary text-xs" data-testid={`sub-submit-${assignment.id}`}>
              {busy ? "…" : t.submit}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- Teacher Dashboard ---------- */
function TeacherDashboard({ t }) {
  const [tab, setTab] = useState("courses");
  return (
    <div className="mt-6" data-testid="teacher-dashboard">
      <div className="flex gap-1 bg-[#F2EBE5] rounded-full p-1 w-fit">
        <TabBtn active={tab === "courses"} onClick={() => setTab("courses")} testid="t-tab-courses">{t.my_courses}</TabBtn>
        <TabBtn active={tab === "inbox"} onClick={() => setTab("inbox")} testid="t-tab-inbox">{t.grading_inbox}</TabBtn>
      </div>
      {tab === "courses" ? <TeacherCourses t={t} /> : <TeacherInbox t={t} />}
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className={`px-4 py-1.5 rounded-full text-sm font-semibold ${active ? "bg-[#2C4A3B] text-[#F9F6F0]" : "text-[#5C564D]"}`}>
      {children}
    </button>
  );
}

function TeacherCourses({ t }) {
  const [courses, setCourses] = useState([]);
  const [creating, setCreating] = useState(false);
  const [editCourse, setEditCourse] = useState(null);
  const { user } = useAuth();

  const load = useCallback(async () => {
    try {
      // Fetch drafts + published for the signed-in teacher. Backend list endpoint supports empty status.
      const [pub, drafts] = await Promise.all([
        api.get("/setti/courses", { params: { status: "published" } }),
        api.get("/setti/courses", { params: { status: "draft" } }),
      ]);
      const all = [...(drafts.data || []), ...(pub.data || [])];
      setCourses(all.filter((c) => c.teacher_id === user?.id || user?.role === "admin"));
    } catch (e) { console.error(e); }
  }, [user]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="mt-4">
      <button onClick={() => setCreating(true)} className="samaaj-btn-primary text-sm" data-testid="btn-create-course">
        + {t.create_course}
      </button>
      <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {courses.map((c) => (
          <div key={c.id} className="samaaj-card p-4" data-testid={`teacher-course-${c.id}`}>
            <div className="flex items-center justify-between">
              <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${c.status === "published" ? "bg-[#E6F0E8] text-[#2C4A3B]" : "bg-[#F2EBE5] text-[#5C564D]"}`}>
                {c.status === "published" ? t.status_published : t.status_draft}
              </span>
              <span className="text-[10px] text-[#5C564D]">{c.lesson_count} lessons</span>
            </div>
            <div className="font-heading text-lg mt-2">{c.title}</div>
            <div className="text-xs text-[#5C564D]">{c.category}</div>
            <button onClick={() => setEditCourse(c)} className="samaaj-btn-outline text-xs mt-3 w-full" data-testid={`btn-edit-${c.id}`}>Edit</button>
          </div>
        ))}
        {courses.length === 0 && <div className="text-sm text-[#5C564D] col-span-full">No courses yet. Click "{t.create_course}" above.</div>}
      </div>
      {creating && <CourseEditor onClose={() => { setCreating(false); load(); }} t={t} />}
      {editCourse && <CourseEditor course={editCourse} onClose={() => { setEditCourse(null); load(); }} t={t} />}
    </div>
  );
}

function CourseEditor({ course, onClose, t }) {
  const [form, setForm] = useState({
    title: course?.title || "", category: course?.category || "Computer",
    description: course?.description || "", duration_weeks: course?.duration_weeks || 4,
    level: course?.level || "Beginner", cover: course?.cover || "", language: course?.language || "en",
  });
  const [courseId, setCourseId] = useState(course?.id || null);
  const [tab, setTab] = useState("info");
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadDetail = useCallback(async () => {
    if (!courseId) return;
    const { data } = await api.get(`/setti/courses/${courseId}`);
    setDetail(data);
  }, [courseId]);
  useEffect(() => { loadDetail(); }, [loadDetail]);

  const save = async () => {
    setBusy(true);
    try {
      if (courseId) { await api.put(`/setti/courses/${courseId}`, form); toast.success("Saved"); }
      else {
        const { data } = await api.post("/setti/courses", form);
        setCourseId(data.id); toast.success("Course created — now add lessons");
        setTab("lessons");
      }
      loadDetail();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const togglePublish = async () => {
    try {
      if (detail.status === "published") { await api.post(`/setti/courses/${courseId}/unpublish`); toast.success("Unpublished"); }
      else { await api.post(`/setti/courses/${courseId}/publish`); toast.success("Published!"); }
      loadDetail();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#1C1B1A]/60 flex items-center justify-center p-4 overflow-y-auto">
      <div className="samaaj-card max-w-3xl w-full p-6 bg-[#F9F6F0] my-8" data-testid="course-editor">
        <div className="flex items-start justify-between">
          <div className="font-heading text-2xl">{courseId ? "Edit course" : t.create_course}</div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-[#F2EBE5]"><X size={18} /></button>
        </div>
        {courseId && (
          <div className="mt-4 flex gap-1 bg-[#F2EBE5] rounded-full p-1 w-fit">
            <TabBtn active={tab === "info"} onClick={() => setTab("info")} testid="ce-tab-info">Info</TabBtn>
            <TabBtn active={tab === "lessons"} onClick={() => setTab("lessons")} testid="ce-tab-lessons">Lessons</TabBtn>
            <TabBtn active={tab === "assignments"} onClick={() => setTab("assignments")} testid="ce-tab-assignments">Assignments</TabBtn>
            <TabBtn active={tab === "assessment"} onClick={() => setTab("assessment")} testid="ce-tab-assessment">Assessment</TabBtn>
          </div>
        )}
        {tab === "info" && (
          <div className="mt-4 space-y-3">
            <input className="w-full px-3 py-2 rounded-xl border border-[#E5D9CC]" placeholder="Course title" value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="ce-title" />
            <div className="grid sm:grid-cols-2 gap-3">
              <select className="px-3 py-2 rounded-xl border border-[#E5D9CC]" value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="ce-category">
                {CATEGORIES.filter((c) => c !== "All").map((c) => <option key={c}>{c}</option>)}
              </select>
              <select className="px-3 py-2 rounded-xl border border-[#E5D9CC]" value={form.level}
                onChange={(e) => setForm({ ...form, level: e.target.value })} data-testid="ce-level">
                <option>Beginner</option><option>Intermediate</option><option>Advanced</option>
              </select>
              <input type="number" min={1} className="px-3 py-2 rounded-xl border border-[#E5D9CC]" placeholder="Duration (weeks)"
                value={form.duration_weeks} onChange={(e) => setForm({ ...form, duration_weeks: parseInt(e.target.value) || 1 })} data-testid="ce-weeks" />
              <input className="px-3 py-2 rounded-xl border border-[#E5D9CC]" placeholder="Cover image URL"
                value={form.cover} onChange={(e) => setForm({ ...form, cover: e.target.value })} data-testid="ce-cover" />
            </div>
            <textarea rows={4} className="w-full px-3 py-2 rounded-xl border border-[#E5D9CC]" placeholder="Description"
              value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="ce-desc" />
            <div className="flex items-center gap-2">
              <button onClick={save} disabled={busy} className="samaaj-btn-primary text-sm" data-testid="ce-save">{busy ? "…" : t.save}</button>
              {detail && (
                <button onClick={togglePublish} className="samaaj-btn-outline text-sm" data-testid="ce-publish">
                  {detail.status === "published" ? t.unpublish : t.publish}
                </button>
              )}
            </div>
          </div>
        )}
        {tab === "lessons" && detail && <LessonEditor courseId={courseId} detail={detail} onRefresh={loadDetail} t={t} />}
        {tab === "assignments" && detail && <AssignmentEditor courseId={courseId} detail={detail} onRefresh={loadDetail} t={t} />}
        {tab === "assessment" && detail && <AssessmentEditor courseId={courseId} t={t} />}
      </div>
    </div>
  );
}

function LessonEditor({ courseId, detail, onRefresh, t }) {
  const [form, setForm] = useState({ title: "", content: "", video_url: "", order: detail.lessons.length, is_mandatory: true, topic_tag: "" });
  const add = async () => {
    try {
      await api.post("/setti/lessons", { course_id: courseId, ...form });
      setForm({ title: "", content: "", video_url: "", order: detail.lessons.length + 1, is_mandatory: true, topic_tag: "" });
      onRefresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/setti/lessons/${id}`); onRefresh(); };
  return (
    <div className="mt-4 space-y-3" data-testid="lesson-editor">
      {detail.lessons.map((l, i) => (
        <div key={l.id} className="flex items-start gap-2 p-3 rounded-xl bg-[#F2EBE5]">
          <div className="flex-1">
            <div className="text-sm font-semibold">{i + 1}. {l.title} {!l.is_mandatory && <span className="text-[10px] text-[#5C564D]">(optional)</span>}</div>
            {l.content && <div className="text-xs text-[#5C564D]">{l.content}</div>}
          </div>
          <button onClick={() => del(l.id)} className="text-xs text-[#D95A41]" data-testid={`del-lesson-${l.id}`}>{t.delete}</button>
        </div>
      ))}
      <div className="pt-3 border-t border-[#E5D9CC] space-y-2">
        <input className="w-full px-3 py-2 rounded-xl border border-[#E5D9CC]" placeholder="Lesson title" value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="le-title" />
        <textarea rows={2} className="w-full px-3 py-2 rounded-xl border border-[#E5D9CC]" placeholder="Short content / summary"
          value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} data-testid="le-content" />
        <input className="w-full px-3 py-2 rounded-xl border border-[#E5D9CC]" placeholder="Video URL (optional)"
          value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} data-testid="le-video" />
        <div className="flex items-center gap-3 flex-wrap">
          <input className="px-3 py-2 rounded-xl border border-[#E5D9CC] text-sm" placeholder="Topic tag"
            value={form.topic_tag} onChange={(e) => setForm({ ...form, topic_tag: e.target.value })} data-testid="le-topic" />
          <label className="text-sm flex items-center gap-1">
            <input type="checkbox" checked={form.is_mandatory} onChange={(e) => setForm({ ...form, is_mandatory: e.target.checked })} data-testid="le-mandatory" />
            {t.mandatory}
          </label>
          <button onClick={add} disabled={!form.title} className="samaaj-btn-primary text-xs ml-auto" data-testid="le-add">+ {t.add_lesson}</button>
        </div>
      </div>
    </div>
  );
}

function AssignmentEditor({ courseId, detail, onRefresh, t }) {
  const [form, setForm] = useState({ title: "", instructions: "", kind: "text", is_mandatory: true, manual_grading: true, passing_required: true, topic_tag: "", order: detail.assignments.length });
  const add = async () => {
    try {
      await api.post("/setti/assignments", { course_id: courseId, ...form });
      setForm({ title: "", instructions: "", kind: "text", is_mandatory: true, manual_grading: true, passing_required: true, topic_tag: "", order: detail.assignments.length + 1 });
      onRefresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/setti/assignments/${id}`); onRefresh(); };
  return (
    <div className="mt-4 space-y-3" data-testid="assignment-editor">
      {detail.assignments.map((a) => (
        <div key={a.id} className="flex items-start gap-2 p-3 rounded-xl bg-[#F2EBE5]">
          <div className="flex-1">
            <div className="text-sm font-semibold">{a.title}</div>
            <div className="text-xs text-[#5C564D]">{a.kind} · {a.is_mandatory ? t.mandatory : t.optional}</div>
          </div>
          <button onClick={() => del(a.id)} className="text-xs text-[#D95A41]" data-testid={`del-assignment-${a.id}`}>{t.delete}</button>
        </div>
      ))}
      <div className="pt-3 border-t border-[#E5D9CC] space-y-2">
        <input className="w-full px-3 py-2 rounded-xl border border-[#E5D9CC]" placeholder="Assignment title" value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="ae-title" />
        <textarea rows={2} className="w-full px-3 py-2 rounded-xl border border-[#E5D9CC]" placeholder="Instructions"
          value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })} data-testid="ae-instr" />
        <div className="flex items-center gap-3 flex-wrap">
          <select className="px-3 py-2 rounded-xl border border-[#E5D9CC] text-sm" value={form.kind}
            onChange={(e) => setForm({ ...form, kind: e.target.value })} data-testid="ae-kind">
            <option value="text">Text</option><option value="file">File</option><option value="practical">Practical</option>
          </select>
          <input className="px-3 py-2 rounded-xl border border-[#E5D9CC] text-sm" placeholder="Topic tag"
            value={form.topic_tag} onChange={(e) => setForm({ ...form, topic_tag: e.target.value })} data-testid="ae-topic" />
          <label className="text-sm flex items-center gap-1">
            <input type="checkbox" checked={form.is_mandatory} onChange={(e) => setForm({ ...form, is_mandatory: e.target.checked })} data-testid="ae-mandatory" />
            {t.mandatory}
          </label>
          <button onClick={add} disabled={!form.title} className="samaaj-btn-primary text-xs ml-auto" data-testid="ae-add">+ {t.add_assignment}</button>
        </div>
      </div>
    </div>
  );
}

function AssessmentEditor({ courseId, t }) {
  const [bank, setBank] = useState({ questions: [], questions_per_attempt: 10, title: "Final Assessment" });
  const [q, setQ] = useState({ text: "", options: ["", "", "", ""], correct_index: 0, topic_tag: "" });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/setti/assessments/${courseId}/mine`);
      setBank({
        questions: data.questions || [],
        questions_per_attempt: data.questions_per_attempt || 10,
        title: data.title || "Final Assessment",
      });
    } catch (e) { /* new */ }
  }, [courseId]);
  useEffect(() => { load(); }, [load]);

  const addQ = () => {
    if (!q.text || q.options.some((o) => !o)) { toast.error("Fill question and all options"); return; }
    setBank({ ...bank, questions: [...bank.questions, q] });
    setQ({ text: "", options: ["", "", "", ""], correct_index: 0, topic_tag: "" });
  };
  const save = async () => {
    try {
      const payload = {
        course_id: courseId, title: bank.title,
        questions_per_attempt: bank.questions_per_attempt,
        questions: bank.questions.map(({ text, options, correct_index, topic_tag }) => ({ text, options, correct_index, topic_tag: topic_tag || "" })),
      };
      await api.put("/setti/assessments", payload);
      toast.success("Assessment saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="mt-4 space-y-3" data-testid="assessment-editor">
      <div className="flex gap-2 items-center">
        <label className="text-sm">Questions per attempt</label>
        <input type="number" min={1} className="w-20 px-2 py-1 rounded-xl border border-[#E5D9CC]" value={bank.questions_per_attempt}
          onChange={(e) => setBank({ ...bank, questions_per_attempt: parseInt(e.target.value) || 1 })} data-testid="asm-per-attempt" />
        <span className="text-xs text-[#5C564D]">Bank: {bank.questions.length}</span>
        <button onClick={save} className="samaaj-btn-primary text-xs ml-auto" data-testid="asm-save">{t.save}</button>
      </div>
      <div className="space-y-2">
        {bank.questions.map((qq, i) => (
          <div key={i} className="p-2 rounded-xl bg-[#F2EBE5] text-xs">
            <div className="font-semibold">{i + 1}. {qq.text}</div>
            {qq.options.map((o, j) => <div key={j} className={j === qq.correct_index ? "text-[#2C4A3B] font-bold" : ""}>  {String.fromCharCode(65 + j)}. {o}</div>)}
            <button onClick={() => setBank({ ...bank, questions: bank.questions.filter((_, k) => k !== i) })} className="text-[#D95A41] mt-1" data-testid={`asm-del-${i}`}>Remove</button>
          </div>
        ))}
      </div>
      <div className="pt-3 border-t border-[#E5D9CC] space-y-2">
        <input className="w-full px-3 py-2 rounded-xl border border-[#E5D9CC]" placeholder="Question text" value={q.text}
          onChange={(e) => setQ({ ...q, text: e.target.value })} data-testid="asm-q-text" />
        {q.options.map((o, i) => (
          <div key={i} className="flex items-center gap-2">
            <input type="radio" checked={q.correct_index === i} onChange={() => setQ({ ...q, correct_index: i })} data-testid={`asm-correct-${i}`} />
            <input className="flex-1 px-3 py-2 rounded-xl border border-[#E5D9CC] text-sm" placeholder={`Option ${String.fromCharCode(65 + i)}`}
              value={o} onChange={(e) => setQ({ ...q, options: q.options.map((x, k) => k === i ? e.target.value : x) })} data-testid={`asm-opt-${i}`} />
          </div>
        ))}
        <input className="w-full px-3 py-2 rounded-xl border border-[#E5D9CC] text-sm" placeholder="Topic tag (optional)" value={q.topic_tag}
          onChange={(e) => setQ({ ...q, topic_tag: e.target.value })} data-testid="asm-q-tag" />
        <button onClick={addQ} className="samaaj-btn-outline text-xs" data-testid="asm-add">+ Add question</button>
      </div>
    </div>
  );
}

function TeacherInbox({ t }) {
  const [subs, setSubs] = useState([]);
  const [status, setStatus] = useState("pending");
  const load = useCallback(async () => {
    try { const { data } = await api.get("/setti/teacher/grading-inbox", { params: { status } }); setSubs(data); }
    catch (e) { console.error(e); }
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const grade = async (sub, grade_status) => {
    const notes = window.prompt("Teacher notes (optional):", "") || "";
    try {
      await api.post("/setti/submissions/grade", {
        submission_id: sub.id, status: grade_status, teacher_notes: notes, weak_topics: [], recommended_lessons: [],
      });
      toast.success("Graded"); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="mt-4" data-testid="teacher-inbox">
      <div className="flex gap-1 bg-[#F2EBE5] rounded-full p-1 w-fit mb-3">
        {["pending", "needs_revision", "pass", "fail", "all"].map((s) => (
          <TabBtn key={s} active={status === s} onClick={() => setStatus(s)} testid={`inbox-${s}`}>{s}</TabBtn>
        ))}
      </div>
      <div className="space-y-3">
        {subs.length === 0 && <div className="text-sm text-[#5C564D]">No submissions.</div>}
        {subs.map((s) => (
          <div key={s.id} className="samaaj-card p-4" data-testid={`inbox-sub-${s.id}`}>
            <div className="flex items-start justify-between flex-wrap gap-2">
              <div>
                <div className="font-semibold text-sm">{s.user_name} · {s.assignment_title}</div>
                <div className="text-xs text-[#5C564D]">{s.course_title}</div>
              </div>
              <div className="text-[10px] uppercase font-bold text-[#5C564D]">{s.grade_status}</div>
            </div>
            {s.text_response && <div className="mt-2 text-xs bg-[#F2EBE5] rounded-lg p-2 whitespace-pre-wrap">{s.text_response}</div>}
            {s.file_url && <a href={s.file_url} target="_blank" rel="noreferrer" className="text-xs text-[#D95A41]">📎 File</a>}
            <div className="mt-2 flex gap-2 flex-wrap">
              <button onClick={() => grade(s, "complete")} className="samaaj-btn-primary text-xs" data-testid={`grade-pass-${s.id}`}>Approve</button>
              <button onClick={() => grade(s, "needs_revision")} className="samaaj-btn-outline text-xs" data-testid={`grade-rev-${s.id}`}>Needs revision</button>
              <button onClick={() => grade(s, "fail")} className="samaaj-btn-outline text-xs" data-testid={`grade-fail-${s.id}`}>Fail</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
